import { Col, DatePicker, Form, Input, message, Modal, Row } from "antd";
import { Rule } from "antd/lib/form";
import { staffApi } from "api/staff.api";
import { todoApi } from "api/todo.api";
import { AppButton, AppSelect } from "components/antd-ui";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import { ModalStatus } from "types/modal";
import { Staff } from "types/staff";
import { Todo } from "types/todo";
import { can } from "utils/auth";
import { fetchCustomerOptions } from "hooks/useRemoteOptions";
import dayjs, { Dayjs } from "utils/dayjs";

const todoPath = "/todo";
const nameRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const deadlineRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const staffRules: Rule[] = [{ required: true, message: "Bắt buộc" }];

type StaffOption = { label: string; value: number };

interface TodoForm {
  name: string;
  deadlineAt?: Dayjs;
  internalNote?: string;
  staffId?: number;
  customerId?: number;
}

export interface TodoModal {
  handleCreate: () => void;
  handleUpdate: (todo: Todo) => void;
}

interface TodoModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

const toForm = (todo: Todo): TodoForm => ({
  name: todo.name,
  deadlineAt: todo.deadlineAt > 0 ? dayjs.unix(todo.deadlineAt) : undefined,
  internalNote: todo.internalNote || "",
  staffId: todo.staff?.id,
  customerId: todo.customer?.id,
});

export const TodoModal = observer(
  React.forwardRef(({ onClose, onSubmitOk }: TodoModalProps, ref) => {
    const [form] = Form.useForm<TodoForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<ModalStatus>("create");
    const [selectedTodo, setSelectedTodo] = useState<Todo>();
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [customerOptions, setCustomerOptions] = useState<StaffOption[]>([]);

    const onlyMine = !userStore.info.role?.isAdmin && can(todoPath, "ONLY_MINE");
    const canUpdate = can(todoPath, "UPDATE");
    const locked = status === "update" && selectedTodo?.status === "DONE";

    const fetchStaff = useCallback(
      async (keyword: string) => {
        const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
        const staffs: Staff[] = res.data.staffs || [];
        setStaffOptions((prev) => {
          const selectedId = form.getFieldValue("staffId");
          const selected = prev.find((option) => option.value === selectedId);
          const options = staffs.map((staff) => ({
            label: staff.name || staff.username,
            value: staff.id,
          }));
          if (selected && !options.some((option) => option.value === selected.value)) {
            return [selected, ...options];
          }
          return options;
        });
      },
      [form],
    );

    const debounceSearchStaff = useMemo(
      () =>
        debounce((keyword: string) => {
          fetchStaff(keyword);
        }, 500),
      [fetchStaff],
    );

    useEffect(() => {
      return () => {
        debounceSearchStaff.cancel();
      };
    }, [debounceSearchStaff]);

    useEffect(() => {
      if (!visible || onlyMine) {
        return;
      }
      fetchStaff("");
    }, [visible, onlyMine, fetchStaff]);

    const fetchCustomer = useCallback(
      async (keyword: string) => {
        const options = await fetchCustomerOptions(keyword);
        setCustomerOptions((prev) => {
          const selectedId = form.getFieldValue("customerId");
          const selected = prev.find((option) => option.value === selectedId);
          if (selected && !options.some((option) => option.value === selected.value)) {
            return [selected, ...options];
          }
          return options;
        });
      },
      [form],
    );

    const debounceSearchCustomer = useMemo(
      () =>
        debounce((keyword: string) => {
          fetchCustomer(keyword);
        }, 500),
      [fetchCustomer],
    );

    useEffect(() => {
      if (!visible) {
        return;
      }
      fetchCustomer("");
      return () => {
        debounceSearchCustomer.cancel();
      };
    }, [visible, fetchCustomer, debounceSearchCustomer]);

    const fillTodo = useCallback(
      (todo: Todo) => {
        form.setFieldsValue(toForm(todo));
        if (todo.staff) {
          setStaffOptions([
            {
              label: todo.staff.name || todo.staff.username,
              value: todo.staff.id,
            },
          ]);
        } else {
          setStaffOptions([]);
        }
        setCustomerOptions(todo.customer ? [{ label: todo.customer.name, value: todo.customer.id }] : []);
        setSelectedTodo(todo);
      },
      [form],
    );

    useImperativeHandle<any, TodoModal>(
      ref,
      () => ({
        handleCreate() {
          form.resetFields();
          setStaffOptions([]);
          setCustomerOptions([]);
          setSelectedTodo(undefined);
          setStatus("create");
          setVisible(true);
        },
        handleUpdate(todo: Todo) {
          fillTodo(todo);
          setStatus("update");
          setVisible(true);
        },
      }),
      [fillTodo, form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelectedTodo(undefined);
      onClose();
    }, [onClose]);

    const buildPayload = useCallback(() => {
      const values = form.getFieldsValue();
      return {
        todo: {
          name: values.name || "",
          deadlineAt: values.deadlineAt ? values.deadlineAt.unix() : 0,
          status: status === "create" ? "NOT_DONE" : selectedTodo?.status || "NOT_DONE",
          internalNote: values.internalNote || "",
        },
        staffId: onlyMine ? undefined : values.staffId,
        customerId: values.customerId || 0,
      };
    }, [form, onlyMine, selectedTodo?.status, status]);

    const handleSubmit = useCallback(async () => {
      await form.validateFields();
      const data = buildPayload();
      setLoading(true);
      try {
        if (status === "create") {
          await todoApi.create(data);
        } else {
          await todoApi.update(selectedTodo?.id || 0, data);
        }
        message.success(status === "create" ? "Tạo mới thành công!" : "Cập nhật thành công!");
        handleClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    }, [buildPayload, form, handleClose, onSubmitOk, selectedTodo?.id, status]);

    const handleReopen = useCallback(async () => {
      if (!selectedTodo) {
        return;
      }
      setLoading(true);
      try {
        await todoApi.update(selectedTodo.id, {
          todo: {
            name: selectedTodo.name,
            deadlineAt: selectedTodo.deadlineAt,
            status: "NOT_DONE",
            internalNote: selectedTodo.internalNote || "",
          },
          staffId: selectedTodo.staff?.id,
        });
        message.success("Đã chuyển về chưa làm");
        handleClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    }, [handleClose, onSubmitOk, selectedTodo]);

    const title = status === "create" ? "Tạo việc cần làm" : "Cập nhật việc cần làm";

    return (
      <Modal
        onCancel={handleClose}
        open={visible}
        title={title}
        style={{ top: 20 }}
        width={640}
        confirmLoading={loading}
        footer={
          <div className="flex justify-end gap-2">
            <AppButton onClick={handleClose}>Đóng</AppButton>
            {locked ? (
              canUpdate && (
                <AppButton tone="primary" loading={loading} onClick={handleReopen}>
                  Chuyển về chưa làm
                </AppButton>
              )
            ) : (
              <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                Lưu
              </AppButton>
            )}
          </div>
        }
      >
        <Form layout="vertical" form={form} disabled={locked}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Tên" name="name" rules={nameRules}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Hạn" name="deadlineAt" rules={deadlineRules}>
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder="Chọn hạn" />
              </Form.Item>
            </Col>
            <Col span={12}>
              {onlyMine ? (
                <Form.Item label="Nhân viên sale">
                  <Input value={userStore.info.name || ""} disabled />
                </Form.Item>
              ) : (
                <Form.Item label="Nhân viên sale" name="staffId" rules={staffRules}>
                  <AppSelect
                    showSearch
                    filterOption={false}
                    options={staffOptions}
                    onSearch={locked ? undefined : debounceSearchStaff}
                    placeholder="Chọn nhân viên"
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={24}>
              <Form.Item label="Khách hàng" name="customerId">
                <AppSelect
                  showSearch
                  allowClear
                  filterOption={false}
                  options={customerOptions}
                  onSearch={locked ? undefined : debounceSearchCustomer}
                  placeholder="Chọn khách hàng (không bắt buộc)"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Ghi chú nội bộ" name="internalNote">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    );
  }),
);
